"use client";

import React, { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle, Phone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category?: string;
}

interface EquipmentFAQProps {
  title?: string;
  subtitle?: string;
  faqs?: FAQItem[];
}

const EquipmentFAQ: React.FC<EquipmentFAQProps> = ({
  title = "Frequently Asked Questions",
  subtitle = "Got questions? We've got answers. Find everything you need to know about our equipment rental service.",
  faqs = [
    {
      id: "1",
      question: "What types of equipment do you have available?",
      answer:
        "We offer a comprehensive range including construction equipment, trailers, power tools, and specialized machinery. All equipment meets Australian safety standards and is regularly maintained for optimal performance.",
      category: "Equipment",
    },
    {
      id: "2",
      question: "How much does equipment hire cost?",
      answer:
        "Our equipment hire rates vary by item and duration. We offer competitive daily, weekly, and monthly rates with significant discounts for longer-term bookings. Contact us for a personalized quote.",
      category: "Pricing",
    },
    {
      id: "3",
      question: "Do you deliver and pickup equipment?",
      answer:
        "Yes! We provide delivery and pickup services. Delivery costs $50 and pickup costs $50. Same-day service available subject to availability with flexible timing to suit your schedule.",
      category: "Delivery",
    },
    {
      id: "4",
      question: "What areas do you service?",
      answer:
        "We service across Australia with reliable delivery and pickup services. Our team provides flexible scheduling options to meet your project requirements.",
      category: "Service Area",
    },
    {
      id: "5",
      question: "Is your equipment insured?",
      answer:
        "Yes, all our equipment comes with comprehensive damage insurance included. A $1000 excess applies in the event of damage. This provides peace of mind for your projects.",
      category: "Insurance",
    },
    {
      id: "6",
      question: "How far in advance do I need to book?",
      answer:
        "While we recommend booking in advance for guaranteed availability, we often accommodate same-day bookings subject to equipment availability. Call us to check current availability for your preferred dates.",
      category: "Booking",
    },
    {
      id: "7",
      question: "What do I need to hire equipment?",
      answer:
        "You'll need valid identification and to meet any specific license requirements for certain equipment. We'll provide safety instructions and operational guidance for all hired equipment.",
      category: "Requirements",
    },
    {
      id: "8",
      question: "Do you offer discounts for long-term hire?",
      answer:
        "Absolutely! We offer attractive discounts for weekly and monthly hires, with significant savings compared to daily rates. Contact us for custom long-term pricing on your specific requirements.",
      category: "Discounts",
    },
    {
      id: "9",
      question: "What if I need emergency or after-hours service?",
      answer:
        "We provide emergency support for urgent requirements. Our team is available to assist with equipment issues, urgent deliveries, or any problems with your hired equipment.",
      category: "Emergency",
    },
    {
      id: "10",
      question: "Is your equipment regularly maintained?",
      answer:
        "Yes, all our equipment undergoes regular professional maintenance and safety inspections to ensure optimal performance and safety. We maintain detailed service records and only hire out equipment in excellent condition.",
      category: "Maintenance",
    },
  ],
}) => {
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <HelpCircle className="h-5 w-5 mr-2 text-[var(--color-rr-green)]" />
          {title}
        </h2>

        <p className="text-sm text-muted-foreground mb-6">{subtitle}</p>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
              >
                <h3 className="text-sm font-medium text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0">
                  {openFAQ === faq.id ? (
                    <ChevronUp className="w-4 h-4 text-[var(--color-rr-green)]" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[var(--color-rr-green)]" />
                  )}
                </div>
              </button>

              {/* Answer */}
              {openFAQ === faq.id && (
                <div className="px-4 pb-3">
                  <div className="border-t border-gray-200 pt-3">
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                    {faq.category && (
                      <div className="mt-2">
                        <span className="inline-block px-2 py-1 text-xs font-medium text-white rounded-full bg-[var(--color-rr-light-green)]">
                          {faq.category}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-6 p-4 bg-gradient-to-r from-[var(--color-rr-green)] to-[var(--color-rr-light-green)] rounded-lg text-white text-center">
          <h3 className="text-lg font-bold mb-2">
            Need More Information?
          </h3>
          <p className="text-sm text-green-100 mb-4">
            Contact our expert team for immediate assistance
          </p>

          <a
            href="tel:0477746329"
            className="inline-flex items-center justify-center bg-white text-black font-medium py-2 px-4 rounded-lg transition-all duration-300 hover:shadow-lg space-x-2"
          >
            <Phone className="w-4 h-4" />
            <span>Call 04 77 746 329</span>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default EquipmentFAQ;